import React, { useRef, useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { TrendingUp, BarChart3, FileText, Brain, Layers, ArrowRight, Check, ChevronRight, Star, Zap, Rocket, Download, Send, MessageSquare } from 'lucide-react';

/* ───── tilt hook for mouse-follow effect ───── */
function useTilt(ref) {
  const handleMove = useCallback((e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    ref.current.style.transform = `perspective(1000px) rotateY(${x * 8}deg) rotateX(${-y * 8}deg) scale3d(1.02,1.02,1.02)`;
  }, [ref]);
  const handleLeave = useCallback(() => {
    if (!ref.current) return;
    ref.current.style.transform = 'perspective(1000px) rotateY(0deg) rotateX(0deg) scale3d(1,1,1)';
  }, [ref]);
  return { onMouseMove: handleMove, onMouseLeave: handleLeave };
}

/* ───── animated counter ───── */
function Counter({ end, suffix = '', duration = 2000 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        let start = 0;
        const step = end / (duration / 16);
        const id = setInterval(() => {
          start += step;
          if (start >= end) { setVal(end); clearInterval(id); }
          else setVal(Math.floor(start));
        }, 16);
        obs.disconnect();
      }
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [end, duration]);
  return <span ref={ref}>{val}{suffix}</span>;
}

/* ───── Screenshot in browser frame ───── */
function BrowserFrame({ src, alt, className = '' }) {
  const frameRef = useRef(null);
  const tilt = useTilt(frameRef);
  return (
    <div ref={frameRef} {...tilt}
      className={`rounded-xl overflow-hidden border border-[#1e3a5f]/60 shadow-2xl shadow-blue-500/10 transition-transform duration-300 ease-out ${className}`}
      style={{ transformStyle: 'preserve-3d' }}>
      {/* browser bar */}
      <div className="bg-[#0a1628] px-4 py-2.5 flex items-center gap-2 border-b border-[#1e3a5f]/60">
        <span className="w-3 h-3 rounded-full bg-red-500/80" />
        <span className="w-3 h-3 rounded-full bg-yellow-500/80" />
        <span className="w-3 h-3 rounded-full bg-green-500/80" />
        <div className="ml-3 flex-1 bg-[#0d1f38] rounded-md px-3 py-1 text-xs text-gray-500 font-mono">baluad.com / dashboard</div>
      </div>
      <img src={src} alt={alt} className="w-full block" loading="lazy" />
    </div>
  );
}

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const go = (path) => () => navigate(path);
  const handleCTA = () => navigate(user ? '/app' : '/login');

  const features = [
    { icon: Brain, title: 'Analista IA que genera tu proyecto', desc: 'Describe tu negocio en palabras simples y la IA estructura todo: inversiones, productos, costos, gastos fijos, financiamiento y meses de operacion.', color: 'from-violet-500 to-purple-500' },
    { icon: TrendingUp, title: 'TIR, VAN, Payback y WACC', desc: 'Todos los indicadores que un banco o inversionista necesita ver, calculados automaticamente a partir de tus datos.', color: 'from-blue-500 to-cyan-500' },
    { icon: Layers, title: 'Escenarios y punto de equilibrio', desc: 'Simula condiciones optimistas y pesimistas. Conoce cuantas unidades necesitas vender para no perder.', color: 'from-purple-500 to-blue-500' },
    { icon: BarChart3, title: 'Dashboard visual interactivo', desc: 'Graficos de ingresos, gastos, flujo de caja y metricas clave que cuentan la historia de tu proyecto de forma clara.', color: 'from-cyan-500 to-teal-500' },
    { icon: FileText, title: 'Reporte PDF ejecutivo', desc: 'Genera un documento profesional listo para presentar ante bancos, inversores o socios con todos los analisis.', color: 'from-blue-500 to-indigo-500' },
    { icon: Download, title: 'Excel tipo banca de inversion', desc: 'Descarga un Excel con 13 hojas estilizadas: inversiones, ER, flujo, valuacion, escenarios y mas.', color: 'from-emerald-500 to-cyan-500' },
  ];

  const steps = [
    { num: '01', icon: '💬', title: 'Describe tu idea de negocio', desc: 'Cuentale a la IA que negocio quieres poner, donde, cuanto piensas invertir y que productos o servicios ofrecerias.' },
    { num: '02', icon: '🧠', title: 'La IA genera tu modelo completo', desc: 'En segundos, estructura inversiones, costos, gastos fijos, productos con precios y toda la proyección financiera a 5 años.' },
    { num: '03', icon: '📊', title: 'Revisa, ajusta y explora', desc: 'Navega por el dashboard interactivo. La IA te explica cada numero y puedes pedirle que modifique lo que necesites.' },
    { num: '04', icon: '📄', title: 'Descarga y presenta', desc: 'Exporta un PDF ejecutivo o un Excel con 13 hojas tipo banca de inversion, listo para presentar a bancos o inversores.' },
  ];

  const staticTestimonials = [
    { name: 'Camila Rodriguez', role: 'Fundadora · Cafeteria Luma', text: 'Necesitaba presentar un análisis financiero al banco para solicitar un crédito. Con Baluad armé el estado de resultados, flujo de caja y los indicadores de TIR y VAN en una tarde. El reporte PDF quedó profesional.' },
    { name: 'Diego Fuentes', role: 'Consultor financiero', text: 'Lo uso para evaluar proyectos de mis clientes. Me ahorra horas de trabajo en Excel porque ya calcula depreciación, amortización, punto de equilibrio y genera los escenarios automáticamente.' },
    { name: 'Valentina Torres', role: 'Emprendedora · Sector alimentos', text: 'Me encanta que puedo ver el dashboard con los gráficos de ingresos vs gastos y el flujo acumulado. También descargo el Excel con las 13 hojas y lo adjunto a mis presentaciones.' },
  ];

  const [dbTestimonials, setDbTestimonials] = useState([]);
  const [feedbackForm, setFeedbackForm] = useState({ name: '', email: '', type: 'feedback', message: '' });
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetch(`${API}/api/testimonials?approved_only=true`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setDbTestimonials(data); })
      .catch(() => {});
  }, [API]);

  const allTestimonials = [...dbTestimonials.map(t => ({ name: t.name, role: t.role, text: t.text })), ...staticTestimonials];

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!feedbackForm.name || !feedbackForm.email || !feedbackForm.message) return;
    setFeedbackSending(true);
    try {
      await fetch(`${API}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(feedbackForm)
      });
      setFeedbackSent(true);
      setFeedbackForm({ name: '', email: '', type: 'feedback', message: '' });
    } catch { }
    setFeedbackSending(false);
  };

  const plans = [
    { id: 'free', name: 'Gratis', price: '$0', period: '', desc: 'Explora el modelo demo', features: ['Visualizar proyecto demo completo', 'Dashboard con KPIs', 'Crear proyectos manualmente', 'Sin acceso a modulos avanzados'], free: true },
    { id: 'monthly', name: 'Mensual', price: '$14', period: 'USD/mes', desc: 'Analista IA + todos los modulos', features: ['Proyectos ilimitados', 'Analista IA incluido', '10 generaciones con IA/mes', '10 modificaciones con IA/mes', 'Exportar PDF y Excel'] },
    { id: 'annual', name: 'Anual', price: '$67', period: 'USD/año', desc: 'Mejor valor — ahorra 60%', features: ['Todo del plan mensual', '10 generaciones con IA/mes', '10 modificaciones con IA/mes', 'Modelo Excel Banca de Inversion', 'Soporte VIP'], popular: true },
  ];

  return (
    <div className="min-h-screen bg-[#060d1b] text-gray-100 overflow-x-hidden">
      {/* ─── CSS animations ─── */}
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(30px) } to { opacity:1; transform:translateY(0) } }
        @keyframes glow { 0%,100% { box-shadow:0 0 20px rgba(59,130,246,.15) } 50% { box-shadow:0 0 40px rgba(59,130,246,.25) } }
        .fade-up { animation: fadeUp .7s ease-out both }
        .fade-up-d1 { animation-delay:.1s } .fade-up-d2 { animation-delay:.2s } .fade-up-d3 { animation-delay:.3s } .fade-up-d4 { animation-delay:.4s }
        .card-hover { transition: all .3s ease }
        .card-hover:hover { transform: translateY(-6px); border-color: rgba(59,130,246,.4); box-shadow: 0 12px 40px rgba(59,130,246,.12) }
        .glow-border { animation: glow 3s ease-in-out infinite }
        .gradient-text { background: linear-gradient(135deg, #818cf8, #6366f1, #38bdf8); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text }
        .gradient-text-green { background: linear-gradient(135deg, #34d399, #06b6d4); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text }
      `}</style>

      {/* ═══════ NAVBAR ═══════ */}
      <nav className="fixed top-0 w-full z-50 bg-[#060d1b]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <img src="/logo-baluad.png" alt="Baluad" className="h-7 object-contain" />
          <div className="flex items-center gap-5">
            <a href="#caracteristicas" className="hidden sm:block text-sm text-gray-400 hover:text-white transition-colors">Características</a>
            <a href="#precios" className="hidden sm:block text-sm text-gray-400 hover:text-white transition-colors">Precios</a>
            <a href="#contacto" className="hidden sm:block text-sm text-gray-400 hover:text-white transition-colors">Contacto</a>
            {user ? (
              <Button onClick={go('/app')} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-5" data-testid="nav-go-app">Ir a la App</Button>
            ) : (
              <Button onClick={go('/login')} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-5" data-testid="nav-login-btn">Ingresar</Button>
            )}
          </div>
        </div>
      </nav>

      {/* ═══════ HERO ═══════ */}
      <section className="pt-32 pb-8 px-6">
        <div className="max-w-5xl mx-auto text-center fade-up">
          {/* badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-400 text-sm font-medium mb-8">
            <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
            NUEVO · ANALISIS CON IA INTEGRADA
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.1] mb-6 tracking-tight">
            Describe tu negocio,<br />
            <span className="gradient-text">la IA hace el resto</span>
          </h1>

          <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Dile a nuestra IA que negocio quieres poner y en segundos
            genera tu modelo financiero completo: inversiones, costos, ingresos,
            TIR, VAN, Payback y flujo de caja — listo para presentar.
          </p>

          <div className="flex gap-4 justify-center flex-wrap mb-8">
            <Button onClick={handleCTA} size="lg"
              className="gap-2 h-13 px-8 text-base bg-blue-600 hover:bg-blue-700 rounded-full shadow-lg shadow-blue-600/25 transition-all hover:shadow-blue-600/40 hover:scale-105"
              data-testid="hero-cta-btn">
              Comenzar ahora <ArrowRight className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex items-center justify-center gap-6 text-sm text-gray-500 mb-16">
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> Modelo de prueba incluido</span>
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> Resultados en 5 minutos</span>
            <span className="hidden sm:flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> PDF y Excel listos para presentar</span>
          </div>
        </div>

        {/* Dashboard screenshot in browser frame */}
        <div className="max-w-5xl mx-auto fade-up fade-up-d2">
          <BrowserFrame src="/screenshots/dashboard.jpg" alt="Baluad Dashboard" />
        </div>
      </section>

      {/* ═══════ STATS BAR ═══════ */}
      <section className="py-20 px-6 border-t border-white/5">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { val: 12, suffix: '', label: 'Modulos de analisis' },
            { val: 7, suffix: '', label: 'Indicadores financieros clave' },
            { val: 13, suffix: '', label: 'Hojas en el modelo Excel' },
            { val: 3, suffix: '', label: 'Escenarios de simulacion' },
          ].map((s, i) => (
            <div key={i}>
              <div className="text-3xl sm:text-4xl font-bold text-white mb-1"><Counter end={s.val} suffix={s.suffix} /></div>
              <div className="text-sm text-gray-500">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════ FEATURES ═══════ */}
      <section id="caracteristicas" className="py-24 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 fade-up">
            <div className="inline-block px-4 py-1.5 rounded-full border border-white/10 bg-white/5 text-gray-400 text-sm font-medium mb-6">CARACTERISTICAS</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Todo lo que necesitas para <span className="gradient-text">tomar decisiones</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Herramientas de nivel empresarial que antes requerian consultores costosos, ahora al alcance de cualquier emprendedor.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <div key={i} className="card-hover p-6 rounded-2xl border border-white/8 bg-[#0a1628]/80 backdrop-blur-sm group">
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center mb-5 opacity-80 group-hover:opacity-100 transition-opacity`}>
                  <f.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-white text-lg mb-2">{f.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ METRICS / SCREENSHOTS ═══════ */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          {/* Estado de Resultados */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-32">
            <div className="fade-up">
              <div className="inline-block px-4 py-1.5 rounded-full border border-white/10 bg-white/5 text-gray-400 text-sm font-medium mb-6">ESTADO DE RESULTADOS</div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Numeros que hablan <span className="gradient-text-green">por tu proyecto</span>
              </h2>
              <p className="text-gray-400 mb-8 leading-relaxed">
                Baluad calcula automáticamente ingresos, EBITDA, utilidad neta y márgenes proyectados año por año. Deja de adivinar y empieza a tomar decisiones con datos reales.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl border border-white/8 bg-[#0a1628]">
                  <div className="text-xs text-emerald-400 font-medium mb-1">UTILIDAD NETA ACUMULADA</div>
                  <div className="text-2xl font-bold text-white">$6.2M</div>
                  <div className="text-xs text-gray-500">5 años proyectados</div>
                </div>
                <div className="p-4 rounded-xl border border-white/8 bg-[#0a1628]">
                  <div className="text-xs text-cyan-400 font-medium mb-1">MARGEN EBITDA</div>
                  <div className="text-2xl font-bold text-white">36.5%</div>
                  <div className="text-xs text-gray-500">Promedio del proyecto</div>
                </div>
              </div>
            </div>
            <div className="fade-up fade-up-d2">
              <BrowserFrame src="/screenshots/er.jpg" alt="Estado de Resultados Baluad" />
            </div>
          </div>

          {/* Flujo de Efectivo */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 fade-up fade-up-d2">
              <BrowserFrame src="/screenshots/flujo.jpg" alt="Flujo de Efectivo Baluad" />
            </div>
            <div className="order-1 lg:order-2 fade-up">
              <div className="inline-block px-4 py-1.5 rounded-full border border-white/10 bg-white/5 text-gray-400 text-sm font-medium mb-6">FLUJO DE EFECTIVO</div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Visualiza cada peso <span className="gradient-text">que entra y sale</span>
              </h2>
              <p className="text-gray-400 mb-8 leading-relaxed">
                Diagrama waterfall, flujo acumulado y tres perspectivas: operativo, proyecto y accionista. Ve exactamente cuando tu proyecto empieza a generar valor.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl border border-white/8 bg-[#0a1628]">
                  <div className="text-xs text-blue-400 font-medium mb-1">TIR DEL PROYECTO</div>
                  <div className="text-2xl font-bold text-white">66.8%</div>
                  <div className="text-xs text-gray-500">+57.3% vs WACC</div>
                </div>
                <div className="p-4 rounded-xl border border-white/8 bg-[#0a1628]">
                  <div className="text-xs text-purple-400 font-medium mb-1">VAN (NPV)</div>
                  <div className="text-2xl font-bold text-white">$3.9M</div>
                  <div className="text-xs text-gray-500">Genera valor</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ HOW IT WORKS ═══════ */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 fade-up">
            <div className="inline-block px-4 py-1.5 rounded-full border border-white/10 bg-white/5 text-gray-400 text-sm font-medium mb-6">COMO FUNCIONA</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
              De la idea al analisis <span className="gradient-text">en 4 pasos</span>
            </h2>
            <p className="text-gray-400 mt-4 max-w-xl mx-auto">Sin conocimientos financieros avanzados. Sin Excel complicado. Solo tu, tu proyecto y resultados claros.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s, i) => (
              <div key={i} className="card-hover relative p-6 rounded-2xl border border-white/8 bg-[#0a1628]/80 group">
                <div className="absolute top-4 right-4 text-5xl font-bold text-white/[.04] select-none">{s.num}</div>
                <div className="text-3xl mb-4">{s.icon}</div>
                <div className="text-blue-400 text-xs font-bold mb-2">{s.num}</div>
                <h3 className="font-semibold text-white mb-2 flex items-center gap-1">
                  {s.title} <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-blue-400 transition-colors" />
                </h3>
                <p className="text-sm text-gray-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ TESTIMONIALS ═══════ */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 fade-up">
            <div className="inline-block px-4 py-1.5 rounded-full border border-white/10 bg-white/5 text-gray-400 text-sm font-medium mb-6">TESTIMONIOS</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
              Lo que dicen <span className="gradient-text">los que ya arrancaron</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {allTestimonials.slice(0, 6).map((t, i) => (
              <div key={i} className="card-hover p-6 rounded-2xl border border-white/8 bg-[#0a1628]/80">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-yellow-500 text-yellow-500" />)}
                </div>
                <p className="text-gray-300 text-sm leading-relaxed mb-6 italic">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                    {t.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <div className="font-semibold text-white text-sm">{t.name}</div>
                    <div className="text-xs text-gray-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ CONTACT / FEEDBACK ═══════ */}
      <section id="contacto" className="py-24 px-6 border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12 fade-up">
            <div className="inline-block px-4 py-1.5 rounded-full border border-white/10 bg-white/5 text-gray-400 text-sm font-medium mb-6">CONTACTO</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              ¿Tienes dudas o <span className="gradient-text">comentarios?</span>
            </h2>
            <p className="text-gray-400">Envíanos tu retroalimentación, sugerencia o cualquier mensaje. Te responderemos lo antes posible.</p>
          </div>

          {feedbackSent ? (
            <div className="text-center p-12 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 fade-up" data-testid="feedback-success">
              <Check className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">¡Mensaje enviado!</h3>
              <p className="text-gray-400 mb-6">Gracias por escribirnos. Revisaremos tu mensaje pronto.</p>
              <Button onClick={() => setFeedbackSent(false)} variant="outline" className="text-white border-white/20 hover:bg-white/5">
                Enviar otro mensaje
              </Button>
            </div>
          ) : (
            <form onSubmit={handleFeedbackSubmit} className="p-8 rounded-2xl border border-white/8 bg-[#0a1628]/80 space-y-5 fade-up" data-testid="feedback-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1.5 block">Nombre</label>
                  <Input
                    value={feedbackForm.name}
                    onChange={(e) => setFeedbackForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="Tu nombre"
                    className="bg-[#0d1f38] border-white/10 text-white placeholder:text-gray-600"
                    data-testid="feedback-name"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1.5 block">Email</label>
                  <Input
                    type="email"
                    value={feedbackForm.email}
                    onChange={(e) => setFeedbackForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="tu@email.com"
                    className="bg-[#0d1f38] border-white/10 text-white placeholder:text-gray-600"
                    data-testid="feedback-email"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Tipo de mensaje</label>
                <div className="flex gap-3">
                  {[
                    { value: 'feedback', label: 'Retroalimentación' },
                    { value: 'suggestion', label: 'Sugerencia' },
                    { value: 'complaint', label: 'Reclamación' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFeedbackForm(p => ({ ...p, type: opt.value }))}
                      className={`px-4 py-2 rounded-lg text-sm transition-all ${
                        feedbackForm.type === opt.value
                          ? 'bg-blue-600 text-white'
                          : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
                      }`}
                      data-testid={`feedback-type-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Mensaje</label>
                <textarea
                  value={feedbackForm.message}
                  onChange={(e) => setFeedbackForm(p => ({ ...p, message: e.target.value }))}
                  placeholder="Escribe tu mensaje aquí..."
                  rows={4}
                  className="w-full rounded-lg bg-[#0d1f38] border border-white/10 text-white placeholder:text-gray-600 p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  data-testid="feedback-message"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={feedbackSending}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-full gap-2"
                data-testid="feedback-submit"
              >
                {feedbackSending ? 'Enviando...' : <><Send className="w-4 h-4" /> Enviar mensaje</>}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* ═══════ PRICING ═══════ */}
      <section id="precios" className="py-24 px-6 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16 fade-up">
            <div className="inline-block px-4 py-1.5 rounded-full border border-white/10 bg-white/5 text-gray-400 text-sm font-medium mb-6">PRECIOS</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Invierte menos que un cafe <span className="gradient-text">al dia</span>
            </h2>
            <p className="text-gray-400">Un consultor cobra $2,000 por lo que Baluad te da en minutos.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((plan) => (
              <div key={plan.id}
                className={`card-hover relative p-8 rounded-2xl border ${plan.popular ? 'border-blue-500/50 glow-border bg-[#0a1628]' : 'border-white/8 bg-[#0a1628]/80'}`}>
                {plan.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 bg-blue-600 text-white text-xs font-bold rounded-full">Mas popular</div>
                )}
                <h3 className="font-semibold text-lg text-white mb-1">{plan.name}</h3>
                <p className="text-sm text-gray-500 mb-5">{plan.desc}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-white">{plan.price}</span>
                  {plan.period && <span className="text-gray-500 ml-1 text-sm">/{plan.period}</span>}
                </div>
                <Button onClick={handleCTA}
                  className={`w-full mb-6 h-11 rounded-full ${plan.popular ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'}`}
                  data-testid={`plan-${plan.id}-btn`}>
                  {plan.free ? 'Acceder Gratis' : 'Comenzar'}
                </Button>
                <ul className="space-y-3">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2.5 text-sm text-gray-300">
                      <Check className="w-4 h-4 text-emerald-400 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ FINAL CTA ═══════ */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="p-12 rounded-3xl bg-gradient-to-b from-[#0a1628] to-[#0d1f38] border border-white/8">
            <div className="text-5xl mb-6"><Rocket className="w-12 h-12 mx-auto text-blue-400" /></div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Tu proyecto merece un analisis <span className="gradient-text">que impresione</span>
            </h2>
            <p className="text-gray-400 mb-8 max-w-lg mx-auto">
              Deja de adivinar y empieza a tomar decisiones con datos reales. Ingresa, explora el modelo de prueba y crea tu propio analisis.
            </p>
            <Button onClick={handleCTA} size="lg"
              className="gap-2 h-13 px-10 text-base bg-blue-600 hover:bg-blue-700 rounded-full shadow-lg shadow-blue-600/25 transition-all hover:shadow-blue-600/40 hover:scale-105"
              data-testid="final-cta-btn">
              Crear mi primer analisis <ArrowRight className="w-4 h-4" />
            </Button>
            <p className="text-xs text-gray-600 mt-4">Modelo de prueba incluido · Desde $14 USD/mes</p>
          </div>
        </div>
      </section>

      {/* ═══════ FOOTER ═══════ */}
      <footer className="border-t border-white/5 py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 mb-8">
            <div className="max-w-sm">
              <img src="/logo-baluad.png" alt="Baluad" className="h-6 object-contain mb-3" />
              <p className="text-sm text-gray-500 leading-relaxed">Plataforma de evaluacion financiera para proyectos de inversion.</p>
            </div>
            <div className="flex gap-6 text-sm text-gray-500">
              <a href="#caracteristicas" className="hover:text-white transition-colors">Características</a>
              <a href="#precios" className="hover:text-white transition-colors">Precios</a>
              <a href="#contacto" className="hover:text-white transition-colors">Contacto</a>
            </div>
          </div>
          <div className="border-t border-white/5 pt-6 text-center text-xs text-gray-600">
            © {new Date().getFullYear()} Baluad. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
